import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  CardTitle as DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import { useFaqDuplicates, useMergeFaqs } from "@/features/faq/hooks/use-faq";
import type { Faq } from "@/features/faq/types";
import { useTranslation } from "@/features/utils/i18n";
import { GitMergeIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { toast } from "sonner";

export const DuplicatesDialog = () => {
  const { t } = useTranslation("faq");
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useFaqDuplicates(open);
  const merge = useMergeFaqs();

  // group index -> chosen canonical (target) FAQ id
  const [selected, setSelected] = useState<Record<number, string>>({});

  const groups = data?.groups || [];

  const handleMerge = async (group: Faq[], index: number) => {
    const targetId = selected[index] || group[0].id;
    const sourceIds = group
      .filter((f) => f.id !== targetId)
      .map((f) => f.id);
    try {
      await merge.mutateAsync({ targetId, sourceIds });
      toast.success(t("duplicates.mergeSuccess"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("duplicates.mergeError"),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <GitMergeIcon className="h-4 w-4" weight="fill" />
          {t("duplicates.button")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("duplicates.title")}</DialogTitle>
          <DialogDescription>{t("duplicates.description")}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : groups.length === 0 ? (
          <p className="text-muted-foreground py-10 text-center text-sm">
            {t("duplicates.none")}
          </p>
        ) : (
          <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
            {groups.map((group, i) => {
              const targetId = selected[i] || group[0].id;
              return (
                <div key={group[0].id} className="space-y-2 rounded-md border p-3">
                  <p className="text-muted-foreground text-xs">
                    {t("duplicates.chooseCanonical")}
                  </p>
                  {group.map((f) => (
                    <label
                      key={f.id}
                      className="flex cursor-pointer items-start gap-2 text-sm"
                    >
                      <input
                        type="radio"
                        name={`dup-group-${i}`}
                        checked={targetId === f.id}
                        onChange={() =>
                          setSelected((s) => ({ ...s, [i]: f.id }))
                        }
                        className="mt-1"
                      />
                      <span className="min-w-0">
                        <span className="font-medium">{f.question}</span>
                        <span className="text-muted-foreground">
                          {" "}
                          · {t("duplicates.askedCount", { count: f.ask_count })}
                        </span>
                      </span>
                    </label>
                  ))}
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => handleMerge(group, i)}
                      disabled={merge.isPending}
                    >
                      <GitMergeIcon className="h-4 w-4" />
                      {t("duplicates.merge")}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
